import * as ex from 'excalibur';
import { Resources } from '../resources';
import { AnimalType, PlantType } from '../types/enums';
import { Entity } from '../core/entity';
import { Animal } from '../core/animal';
import { Plant } from '../core/plant';

/**
 * SpriteManager handles loading and assigning sprites to entities
 */
export class SpriteManager {
  // Track whether resources have been processed
  private static initialized = false;

  /**
   * Initialize the sprite manager
   */
  public static initialize(): void {
    if (this.initialized) return;

    console.log('Initializing SpriteManager...');

    try {
      this.initialized = true;
      console.log('SpriteManager initialized successfully');
    } catch (error) {
      console.error('Error initializing SpriteManager:', error);
    }
  }

  /**
   * Apply a sprite to an entity based on its type
   * @param entity The entity to apply a sprite to
   */
  public static applySprite(entity: Entity): void {
    if (!this.initialized) {
      this.initialize();
    }

    if (entity instanceof Animal) {
      this.applyAnimalSprite(entity);
    } else if (entity instanceof Plant) {
      this.applyPlantSprite(entity);
    }
  }

  /**
   * Apply an animal sprite and animation based on type
   * @param animal The animal to apply a sprite to
   */
  private static applyAnimalSprite(animal: Animal): void {
    switch (animal.animalType) {
      case AnimalType.Bird:
        this.applyBirdSprite(animal);
        break;
      case AnimalType.Wolf:
        this.applyWolfSprite(animal);
        break;
      case AnimalType.Rabbit:
        this.applyRabbitSprite(animal);
        break;
      case AnimalType.Deer:
        this.applyDeerSprite(animal);
        break;
      case AnimalType.Fish:
        this.applyFishSprite(animal);
        break;
      case AnimalType.Bear:
        this.applyBearSprite(animal);
        break;
      default:
        // Use emoji for other animal types
        animal.setEmoji(this.getEmojiForAnimal(animal.animalType));
    }
  }

  /**
   * Apply bird sprite from Aseprite file
   */
  private static applyBirdSprite(animal: Animal): void {
    console.log('Applying bird sprite from Aseprite');

    try {
      if (Resources.Bird && Resources.Bird.isLoaded()) {
        console.log('Bird resource is loaded');

        // First try to get an animation
        try {
          // Get an animation - if there's one named 'Loop' or 'Idle', use that
          let anim = null;

          try {
            anim = Resources.Bird.getAnimation('idle');
            console.log('Found Loop animation');
          } catch (e) {
            console.log('No named animations found, getting as sprite');
          }

          if (anim) {
            // Use animation but don't play it - just use the first frame for better performance
            animal.graphics.use(anim);
            // anim.play(); // Disabled for performance
            console.log('Applied bird sprite frame');
            return;
          }
        } catch (animError) {
          console.warn('Could not get animation:', animError);
        }

        try {
          // Get the Aseprite sprite sheet
          const sprite = Resources.Bird.getSpriteSheet()?.getSprite(0, 0);
          console.log('Using bird sprite');

          // Apply the sprite
          animal.graphics.use(sprite!);
          console.log('Applied bird sprite');
          return;
        } catch (spriteError) {
          console.error('Failed to get sprite:', spriteError);
        }
      } else {
        console.warn('Bird resource not loaded');
      }
      // If all else fails, use emoji fallback
      console.warn('Falling back to emoji for bird');
      animal.setEmoji(this.getEmojiForAnimal(AnimalType.Bird));
    } catch (error) {
      console.error('Error applying bird sprite:', error);
    }
  }

  /**
   * Apply a plant sprite based on type
   * @param plant The plant to apply a sprite to
   */
  private static applyPlantSprite(plant: Plant): void {
    switch (plant.plantType) {
      case PlantType.Grass:
        this.applyGrassSprite(plant);
        break;
      case PlantType.Tree:
        this.applyTreeSprite(plant);
        break;
      case PlantType.Flower:
        this.applyFlowerSprite(plant);
        break;
      case PlantType.Bush:
        this.applySproutSprite(plant);
        break;
      default:
        // Use emoji for other plant types
        plant.setEmoji(this.getEmojiForPlant(plant.plantType));
    }
  }

  /**
   * Get a fallback emoji for an animal type
   * @param animalType The animal type
   * @returns An emoji string
   */
  private static getEmojiForAnimal(animalType: AnimalType): string {
    switch (animalType) {
      case AnimalType.Wolf: return '🐺';
      case AnimalType.Rabbit: return '🐇';
      case AnimalType.Deer: return '🦌';
      case AnimalType.Bird: return '🐦';
      case AnimalType.Fish: return '🐠';
      default: return '❓';
    }
  }

  /**
   * Get a fallback emoji for a plant type
   * @param plantType The plant type
   * @returns An emoji string
   */
  private static getEmojiForPlant(plantType: PlantType): string {
    switch (plantType) {
      case PlantType.Tree: return '🌳';
      case PlantType.Bush: return '🌿';
      case PlantType.Flower: return '🌸';
      case PlantType.Grass: return '🌱';
      default: return '❓';
    }
  }

  /**
   * Apply grass sprite from Aseprite file
   */
  private static applyGrassSprite(plant: Plant): void {
    try {
      // Use dense or sparse grass based on internal properties or randomly
      const usesDenseGrass = Math.random() > 0.5; // 50% chance for either type
      const grassResource = usesDenseGrass ? Resources.GrassDense : Resources.GrassSparse;
      
      if (grassResource && grassResource.isLoaded()) {
        // Try to get an animation
        try {
          let anim = null;
          try {
            anim = grassResource.getAnimation('idle');
          } catch (e) {
            console.log('No named animations found for grass, getting as sprite');
          }

          if (anim) {
            plant.graphics.use(anim);
            // anim.play(); // Disabled for performance
            return;
          }
        } catch (animError) {
          console.warn('Could not get grass animation:', animError);
        }

        try {
          const sprite = grassResource.getSpriteSheet()?.getSprite(0, 0);
          plant.graphics.use(sprite!);
          return;
        } catch (spriteError) {
          console.error('Failed to get grass sprite:', spriteError);
        }
      } else {
        console.warn('Grass resource not loaded');
      }
      // Fallback to emoji
      plant.setEmoji(this.getEmojiForPlant(PlantType.Grass));
    } catch (error) {
      console.error('Error applying grass sprite:', error);
    }
  }

  /**
   * Apply tree sprite from Aseprite file
   */
  private static applyTreeSprite(plant: Plant): void {
    try {
      // Use small or large trees based on internal properties or randomly
      const usesLargeTree = Math.random() > 0.5; // 50% chance for either type
      const treeResource = usesLargeTree ? Resources.TreesLarge : Resources.TreeSmall;
      
      if (treeResource && treeResource.isLoaded()) {
        // Try to get an animation
        try {
          let anim = null;
          try {
            anim = treeResource.getAnimation('idle');
          } catch (e) {
            console.log('No named animations found for tree, getting as sprite');
          }

          if (anim) {
            plant.graphics.use(anim);
            // anim.play(); // Disabled for performance
            return;
          }
        } catch (animError) {
          console.warn('Could not get tree animation:', animError);
        }

        try {
          const sprite = treeResource.getSpriteSheet()?.getSprite(0, 0);
          plant.graphics.use(sprite!);
          return;
        } catch (spriteError) {
          console.error('Failed to get tree sprite:', spriteError);
        }
      } else {
        console.warn('Tree resource not loaded');
      }
      // Fallback to emoji
      plant.setEmoji(this.getEmojiForPlant(PlantType.Tree));
    } catch (error) {
      console.error('Error applying tree sprite:', error);
    }
  }
  
  /**
   * Apply flower sprite from Aseprite file
   */
  private static applyFlowerSprite(plant: Plant): void {
    try {
      if (Resources.Flower && Resources.Flower.isLoaded()) {
        // Try to get an animation
        try {
          let anim = null;
          try {
            anim = Resources.Flower.getAnimation('idle');
          } catch (e) {
            console.log('No named animations found for flower, getting as sprite');
          }

          if (anim) {
            plant.graphics.use(anim);
            // anim.play(); // Disabled for performance
            return;
          }
        } catch (animError) {
          console.warn('Could not get flower animation:', animError);
        }

        try {
          const sprite = Resources.Flower.getSpriteSheet()?.getSprite(0, 0);
          plant.graphics.use(sprite!);
          return;
        } catch (spriteError) {
          console.error('Failed to get flower sprite:', spriteError);
        }
      } else {
        console.warn('Flower resource not loaded');
      }
      // Fallback to emoji
      plant.setEmoji(this.getEmojiForPlant(PlantType.Flower));
    } catch (error) {
      console.error('Error applying flower sprite:', error);
    }
  }

  /**
   * Apply sprout (bush) sprite from Aseprite file
   */
  private static applySproutSprite(plant: Plant): void {
    try {
      // Select mature or young sprout based on growth stage
      const usesMature = plant.growthStage > plant.maxGrowthStage / 2;
      const sproutResource = usesMature ? Resources.SproutMature : Resources.Sprout;
      
      if (sproutResource && sproutResource.isLoaded()) {
        // Try to get an animation
        try {
          let anim = null;
          try {
            anim = sproutResource.getAnimation('idle');
          } catch (e) {
            console.log('No named animations found for sprout, getting as sprite');
          }

          if (anim) {
            plant.graphics.use(anim);
            // anim.play(); // Disabled for performance
            return;
          }
        } catch (animError) {
          console.warn('Could not get sprout animation:', animError);
        }

        try {
          const sprite = sproutResource.getSpriteSheet()?.getSprite(0, 0);
          plant.graphics.use(sprite!);
          return;
        } catch (spriteError) {
          console.error('Failed to get sprout sprite:', spriteError);
        }
      } else {
        console.warn('Sprout resource not loaded');
      }
      // Fallback to emoji
      plant.setEmoji(this.getEmojiForPlant(PlantType.Bush));
    } catch (error) {
      console.error('Error applying sprout sprite:', error);
    }
  }

  /**
   * Apply wolf sprite from Aseprite file
   */
  private static applyWolfSprite(animal: Animal): void {
    try {
      if (Resources.Wolf && Resources.Wolf.isLoaded()) {
        // First try to get an animation
        try {
          let anim = null;
          try {
            anim = Resources.Wolf.getAnimation('idle');
          } catch (e) {
            console.log('No named animations found for wolf, getting as sprite');
          }

          if (anim) {
            animal.graphics.use(anim);
            // anim.play(); // Disabled for performance
            return;
          }
        } catch (animError) {
          console.warn('Could not get wolf animation:', animError);
        }

        try {
          const sprite = Resources.Wolf.getSpriteSheet()?.getSprite(0, 0);
          animal.graphics.use(sprite!);
          return;
        } catch (spriteError) {
          console.error('Failed to get wolf sprite:', spriteError);
        }
      } else {
        console.warn('Wolf resource not loaded');
      }
      // Fallback to emoji
      animal.setEmoji(this.getEmojiForAnimal(AnimalType.Wolf));
    } catch (error) {
      console.error('Error applying wolf sprite:', error);
    }
  }

  /**
   * Apply rabbit sprite from Aseprite file
   */
  private static applyRabbitSprite(animal: Animal): void {
    try {
      if (Resources.Rabbit && Resources.Rabbit.isLoaded()) {
        // First try to get an animation
        try {
          let anim = null;
          try {
            anim = Resources.Rabbit.getAnimation('idle');
          } catch (e) {
            console.log('No named animations found for rabbit, getting as sprite');
          }

          if (anim) {
            animal.graphics.use(anim);
            // anim.play(); // Disabled for performance
            return;
          }
        } catch (animError) {
          console.warn('Could not get rabbit animation:', animError);
        }

        try {
          const sprite = Resources.Rabbit.getSpriteSheet()?.getSprite(0, 0);
          animal.graphics.use(sprite!);
          return;
        } catch (spriteError) {
          console.error('Failed to get rabbit sprite:', spriteError);
        }
      } else {
        console.warn('Rabbit resource not loaded');
      }
      // Fallback to emoji
      animal.setEmoji(this.getEmojiForAnimal(AnimalType.Rabbit));
    } catch (error) {
      console.error('Error applying rabbit sprite:', error);
    }
  }

  /**
   * Apply deer sprite from Aseprite file
   */
  private static applyDeerSprite(animal: Animal): void {
    try {
      if (Resources.Deer && Resources.Deer.isLoaded()) {
        // First try to get an animation
        try {
          let anim = null;
          try {
            anim = Resources.Deer.getAnimation('idle');
          } catch (e) {
            console.log('No named animations found for deer, getting as sprite');
          }

          if (anim) {
            animal.graphics.use(anim);
            // anim.play(); // Disabled for performance
            return;
          }
        } catch (animError) {
          console.warn('Could not get deer animation:', animError);
        }

        try {
          const sprite = Resources.Deer.getSpriteSheet()?.getSprite(0, 0);
          animal.graphics.use(sprite!);
          return;
        } catch (spriteError) {
          console.error('Failed to get deer sprite:', spriteError);
        }
      } else {
        console.warn('Deer resource not loaded');
      }
      // Fallback to emoji
      animal.setEmoji(this.getEmojiForAnimal(AnimalType.Deer));
    } catch (error) {
      console.error('Error applying deer sprite:', error);
    }
  }
  
  /**
   * Apply fish sprite from Aseprite file
   */
  private static applyFishSprite(animal: Animal): void {
    try {
      if (Resources.Fish && Resources.Fish.isLoaded()) {
        // First try to get an animation
        try {
          let anim = null;
          try {
            anim = Resources.Fish.getAnimation('idle');
          } catch (e) {
            console.log('No named animations found for fish, getting as sprite');
          }

          if (anim) {
            animal.graphics.use(anim);
            // anim.play(); // Disabled for performance
            return;
          }
        } catch (animError) {
          console.warn('Could not get fish animation:', animError);
        }

        try {
          const sprite = Resources.Fish.getSpriteSheet()?.getSprite(0, 0);
          animal.graphics.use(sprite!);
          return;
        } catch (spriteError) {
          console.error('Failed to get fish sprite:', spriteError);
        }
      } else {
        console.warn('Fish resource not loaded');
      }
      // Fallback to emoji
      animal.setEmoji(this.getEmojiForAnimal(AnimalType.Fish));
    } catch (error) {
      console.error('Error applying fish sprite:', error);
    }
  }
  
  /**
   * Apply bear sprite from Aseprite file
   */
  private static applyBearSprite(animal: Animal): void {
    try {
      if (Resources.Bear && Resources.Bear.isLoaded()) {
        // First try to get an animation
        try {
          let anim = null;
          try {
            anim = Resources.Bear.getAnimation('idle');
          } catch (e) {
            console.log('No named animations found for bear, getting as sprite');
          }

          if (anim) {
            animal.graphics.use(anim);
            // anim.play(); // Disabled for performance
            return;
          }
        } catch (animError) {
          console.warn('Could not get bear animation:', animError);
        }

        try {
          const sprite = Resources.Bear.getSpriteSheet()?.getSprite(0, 0);
          animal.graphics.use(sprite!);
          return;
        } catch (spriteError) {
          console.error('Failed to get bear sprite:', spriteError);
        }
      } else {
        console.warn('Bear resource not loaded');
      }
      // Fallback to emoji
      animal.setEmoji(this.getEmojiForAnimal(AnimalType.Bear));
    } catch (error) {
      console.error('Error applying bear sprite:', error);
    }
  }
}
